import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export interface ServiceAreasProps {
  headline: string;
  description: string;
  primary: string;
  areas: string[];
  showMap?: boolean;
}

export default function ServiceAreas({ headline, description, primary, areas, showMap }: ServiceAreasProps) {
  const head = useScrollAnimation();
  const body = useScrollAnimation();

  return (
    <section className="bg-white py-24 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          ref={head.ref}
          className="text-center mb-14"
          style={{ opacity: head.visible ? 1 : 0, transform: head.visible ? 'translateY(0)' : 'translateY(24px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-6 h-px bg-jc-gold"></span>
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-jc-gold">Service Areas</span>
            <span className="w-6 h-px bg-jc-gold"></span>
          </div>
          <h2 className="font-serif text-[clamp(2rem,4vw,3.2rem)] font-light text-jc-charcoal leading-[1.12]">{headline}</h2>
          <p className="text-gray-400 text-[0.95rem] mt-5 max-w-lg mx-auto leading-relaxed">{description}</p>
        </div>

        <div
          ref={body.ref}
          className="flex flex-wrap justify-center gap-3 mb-12"
          style={{ opacity: body.visible ? 1 : 0, transition: 'opacity 0.7s ease 0.1s' }}
        >
          <span className="px-6 py-3 bg-jc-charcoal text-white text-[11px] font-bold tracking-[0.15em] uppercase rounded-full">
            {primary} <i className="ri-map-pin-fill text-jc-gold ml-1.5 text-xs"></i>
          </span>
          {areas.filter((a) => a !== primary).map((area, i) => (
            <span
              key={i}
              className="px-5 py-2.5 bg-jc-cream text-jc-charcoal text-sm font-medium rounded-full border border-jc-sage/20 hover:border-jc-gold/60 hover:bg-jc-gold/5 transition-all duration-200"
              style={{
                opacity: body.visible ? 1 : 0,
                transition: `opacity 0.4s ease ${0.1 + i * 0.05}s`,
              }}
            >
              {area}
            </span>
          ))}
        </div>

        {showMap && (
          <div
            className="rounded-2xl overflow-hidden border border-gray-100"
            style={{ height: '420px', opacity: body.visible ? 1 : 0, transition: 'opacity 0.7s ease 0.4s' }}
          >
            <iframe
              title="JC Cooks location in San Antonio"
              src="https://maps.google.com/maps?q=7824+Mainland+Drive+%23106+San+Antonio+Texas+78250&t=&z=14&ie=UTF8&iwloc=&output=embed"
              className="w-full h-full border-0"
              allowFullScreen
              loading="lazy"
            ></iframe>
          </div>
        )}

        <div
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8"
          style={{ opacity: body.visible ? 1 : 0, transition: 'opacity 0.7s ease 0.5s' }}
        >
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <i className="ri-map-pin-line text-jc-gold text-base"></i>
            <span>7824 Mainland Drive, #106, San Antonio, Texas 78250</span>
          </div>
          <a href="tel:2108587293" className="flex items-center gap-2 text-gray-400 text-sm hover:text-jc-charcoal transition-colors">
            <i className="ri-phone-line text-jc-gold text-base"></i>
            <span>210-858-7293</span>
          </a>
        </div>
      </div>
    </section>
  );
}
